import React, { useEffect, useMemo } from 'react';
import PageBanner from '../layout/PageBanner';
import SectionHeading from '../ui/SectionHeading';
import Button from '../ui/Button';
import Card from '../ui/Card';
import { bakeryProducts, bakeryAdvantages } from '../../data/products';
import { useBetaProgram } from '../../beta/hooks';
import { useBetaMixDrafts } from '../../beta/mixDrafts';

const getBakerySampleId = (productId = '') => {
  if (
    productId.includes('lava') ||
    productId.includes('brownie') ||
    productId.includes('velvet') ||
    productId.includes('yellow')
  ) {
    return 'sample-lava-cake';
  }

  return 'sample-mousse';
};

const buildOrderSamplePath = (sampleId, source) => {
  const params = new URLSearchParams();
  params.set('sample', sampleId);
  params.set('from', source);
  return `/samples?${params.toString()}`;
};

const BakeryMixesPage = () => {
  const { programSlug, program } = useBetaProgram();
  const { ensureManagedMixes, getManagedMixes } = useBetaMixDrafts();
  const showSampleOrdering = Boolean(program?.features?.sampleOrderingFlow);
  const showManageAdmin =
    Boolean(program?.features?.easyManageAdmin) || Boolean(program?.features?.easyAddAdmin);

  useEffect(() => {
    if (!showManageAdmin || !programSlug) {
      return;
    }

    ensureManagedMixes(programSlug, 'bakery', bakeryProducts);
  }, [ensureManagedMixes, programSlug, showManageAdmin]);

  const allBakeryProducts = useMemo(() => {
    if (!showManageAdmin) {
      return bakeryProducts;
    }

    return getManagedMixes(programSlug, 'bakery', bakeryProducts);
  }, [getManagedMixes, programSlug, showManageAdmin]);

  return (
    <div>
      <PageBanner
        title="BAKERY MIXES"
        breadcrumbs={[{ label: 'BAKERY MIXES' }]}
      />

      {/* Introduction */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <SectionHeading title="Complete Cake Mixes" />
          <p className="text-gray-600 leading-relaxed">
            Cake mixes from Mirage prepare in minutes with water and oil only.
            Bake times of 20-30 minutes. We formulate our mixes for a minimum of
            prep time and hassle: giving the chef, baker or caterer reliable,
            delectable results every time.
          </p>
          {showSampleOrdering && (
            <div className="mt-6">
              <Button href="/samples" variant="filled">
                Order Samples
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Advantages Section */}
      <section className="py-12 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <SectionHeading title="The Advantages of Using Mirage Bakery Mixes" />
          <p className="text-center text-gray-600 mb-8">
            Chefs, bakers, and caterers choose Mirage bakery mixes over scratch baking for many compelling reasons.
          </p>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bakeryAdvantages.map((advantage, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="w-2 h-2 bg-primary-red rounded-full mt-2 flex-shrink-0" />
                <span className="text-gray-700">{advantage}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {allBakeryProducts.map((product) => {
              const samplePath = buildOrderSamplePath(
                getBakerySampleId(product.id),
                'bakery-mixes'
              );

              return (
                <article key={product.id}>
                  <Card
                    title={product.title}
                    description={product.description}
                    image={product.image}
                  />
                  {showSampleOrdering && (
                    <div className="mt-3">
                      <Button href={samplePath} variant="outlined" className="w-full text-center">
                        Order Sample
                      </Button>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BakeryMixesPage;
